import {
  SubscriptionLineItem,
  useSubscription,
} from "@chordcommerce/react-autonomy";
import { SubALCAddOnProps } from "@bluebottlecoffee/design-system/components";
import { ShopCardPropsWithProduct } from "@bluebottlecoffee/design-system/components/ShopCard/ShopCardTypes";
import { isBefore } from "date-fns";
import { noopPromiseVoid } from "@bluebottlecoffee/design-system/components/utils/noop";
import { toHTML } from "@portabletext/to-html";
import { useMemo } from "react";
import {
  trackSubscriptionALCAddOnVariantAdded,
  trackSubscriptionALCAddOnVariantRemoved,
} from "../segment/track-subscription";
import { toCopyObject } from "../sanity/translation-group-queries";
import useVariantAvailability from "../chord/hooks/components/use-variant-availability";
import { toShopCardProps } from "../transformers/shop-card";
import { SubscriptionAddOnProjection } from "../sanity/current-subscriptions-queries";
import { getPriceText, variantFallbackImageSmall } from "../utils";
import { ConversionSchema } from "../transformers/conversion";
import { isFeatureEnabled } from "../utils/is-feature-enabled";

const showSubALCAddOnFeatureFlag: boolean = isFeatureEnabled(
  process.env.NEXT_PUBLIC_ENABLE_SUBSCRIPTION_ADD_ON,
);

// Unit-agnostic: works for both cents (addOnLineItems) and dollars (useVariantPrice).
const applyDiscount = (amount: number, discountPercent: number): number =>
  Math.round(amount * (1 - discountPercent / 100));

// TODO: Remove when the Chord API provides discounted prices directly.
const buildUseVariantPrice =
  (card: ReturnType<typeof toShopCardProps>, discountPercent: number) =>
  ({ sku }: { sku: string }) => {
    const variant = card.product.variants.find((v) => v.sku === sku);
    const regularPrice = Number(variant?.regularPrice.amount);
    return { discountPrice: applyDiscount(regularPrice, discountPercent) };
  };

interface UseSubALCAddOnProps {
  subscriptionId: number;
  subscriptionAddOn: SubscriptionAddOnProjection;
  region: string;
  lang: string;
}

export const useSubALCAddOns = ({
  subscriptionId,
  subscriptionAddOn,
  region,
  lang,
}: UseSubALCAddOnProps): SubALCAddOnProps => {
  const { subscription, updateSubscription } = useSubscription({
    subscriptionId,
  });

  const showSubALCAddOn: boolean =
    subscription?.state === "active" && !subscription?.prepaidSubscription;

  const isEditable = isBefore(
    new Date(),
    new Date(`${subscription?.actionableDate}T00:00`),
  );

  const addOnLineItems: SubscriptionLineItem[] =
    // @ts-ignore (see comment in handleAddToSub function below)
    subscription?.lineItems.filter((li) => li.oneTime || li.oneTimeAdd) ?? []; // TODO: Chord change this data type errantly ahead of schedule. Adding both data types temporarily to unbreak our site while Chord works to fix on their end.

  const totalPrice = addOnLineItems.map(
    (variant) => Number(variant.price) * 100 * variant.quantity,
  );

  const totalPriceDisplay = getPriceText({
    amount: applyDiscount(
      totalPrice.reduce((a, b) => a + b, 0),
      subscriptionAddOn.discountPercent,
    ),
    currency: "USD",
  }); // TODO: localize currency

  const shopcards = useMemo(
    () =>
      subscriptionAddOn.products.map((card) =>
        toShopCardProps({
          data: card,
          lang,
          region,
          shippingCopy: subscriptionAddOn.shopcardCopy.shippingCopy,
          copy: toCopyObject(lang, subscriptionAddOn.shopcardCopy.copy),
        }),
      ),
    [subscriptionAddOn, lang, region],
  );

  if (!isEditable && addOnLineItems.length === 0) return null;
  if (!showSubALCAddOn) return null;

  const handleModifyQuantity = async (lineItemId: number, quantity: number) => {
    const lineItemsAttributes = subscription.lineItems.map((li) => {
      if (li.id === lineItemId) {
        return {
          ...li,
          quantity,
        };
      }
      return li;
    });

    try {
      await updateSubscription({ lineItemsAttributes });
    } catch (error) {
      throw new Error(error);
    }
  };

  const handleAddToSub = async (sku: string, quantity: number) => {
    const exist = addOnLineItems.find((li) => li.sku === sku);

    if (exist) {
      handleModifyQuantity(exist.id, quantity + exist.quantity);
    } else {
      try {
        const data = await updateSubscription({
          // @ts-ignore Chord SubscriptionLineItem type is missing the oneTime: boolean
          // but the API response includes this. Issue acknowledged by Chord 7 - 8 - 24
          lineItemsAttributes: [{ sku, quantity, oneTime: true }],
        });
        const addedLineItem = data.lineItems.find((li) => li.sku === sku);
        trackSubscriptionALCAddOnVariantAdded(addedLineItem, data);
        return data;
      } catch (error) {
        return error;
      }
    }

    return null;
  };

  const handleRemoveFromSub = async (lineItemId: number) => {
    let removedLineItem: SubscriptionLineItem | undefined;
    const lineItemsAttributes = [
      ...subscription.lineItems.map((li: SubscriptionLineItem) => {
        if (li.id === lineItemId) {
          removedLineItem = li;
          return {
            ...li,
            _destroy: true,
          };
        }
        return li;
      }),
    ];

    try {
      const data = await updateSubscription({ lineItemsAttributes });
      trackSubscriptionALCAddOnVariantRemoved(removedLineItem, data);
    } catch (error) {
      throw new Error(error);
    }
  };

  return {
    readOnly: !isEditable,
    onAddToSub: handleAddToSub,
    onRemove: handleRemoveFromSub,
    onQuantityChange: handleModifyQuantity,
    total: totalPriceDisplay,
    copy: {
      heading: subscriptionAddOn.header[lang],
      savingText: subscriptionAddOn.savingBadgeText[lang],
      headingSelectedAddons: subscriptionAddOn.selectedProductsHeader[lang],
      addOnsTotal: subscriptionAddOn.addOnsTotalLabel[lang],
      infoMessage: toHTML(subscriptionAddOn.description[lang]),
      loadingMessage: subscriptionAddOn.updatingYourAddOnsLabel[lang],
      errorMessage: subscriptionAddOn.genericErrorMessage[lang],
      removeAriaLabel: (itemName: string) =>
        `${subscriptionAddOn.removeAriaLabel[lang]} ${itemName}`,
      quantitySelectorAriaLabel: (variantName: string) =>
        `${subscriptionAddOn.quantitySelectorAriaLabel[lang]} ${variantName}`,
      discountPriceLabel: subscriptionAddOn.discountPriceLabel[lang],
      loadMoreLabel: subscriptionAddOn.loadMoreLabel[lang],
    },
    addOnLineItems: addOnLineItems.map((li) => {
      const baseAmount = Number(li.price) * 100 * li.quantity;
      return {
        ...li,
        currency: "USD",
        price: getPriceText({
          amount: applyDiscount(baseAmount, subscriptionAddOn.discountPercent),
          currency: "USD",
        }),
        originalPrice: getPriceText({ amount: baseAmount, currency: "USD" }),
        image: {
          altText: li.images?.[0]?.alt,
          src: li.images?.[0]?.smallUrl || variantFallbackImageSmall,
        },
      };
    }),
    shopcards: shopcards.map(
      (card) =>
        ({
          ...card,
          useVariantAvailability,
          useVariantPrice: buildUseVariantPrice(
            card,
            subscriptionAddOn.discountPercent,
          ),
          bannerText: undefined,
          // to not show the subscribe settings
          subscription: undefined,
          // strip subscribable props from the product object so they don't
          // surface on the current-subscriptions page
          product: {
            ...card.product,
            subscription: undefined,
            subscriptionOptions: undefined,
          },
          // setting link to undefined because otherwise it will open the PDP
          link: undefined,
          // do not open the side cart
          overrideMiniCart: true,
          // these are required by the ShopCard component, this is used internally by the component(SubALCAddOn)
          addToCart: noopPromiseVoid,
          subscribeProduct: noopPromiseVoid,
          createStockRequest: noopPromiseVoid,
        }) as ShopCardPropsWithProduct,
    ),
  };
};

interface ToSubALCAddOnProps {
  subscriptionAddOn: SubscriptionAddOnProjection;
  region: string;
  lang: string;
}

export const toSubALCAddOn = ({
  subscriptionAddOn,
  region,
  lang,
}: ToSubALCAddOnProps) => {
  if (!showSubALCAddOnFeatureFlag) return undefined;
  if (!subscriptionAddOn) return undefined;

  const subscriptionAddOnProducts: ConversionSchema[] | undefined =
    subscriptionAddOn?.products as unknown as ConversionSchema[] | undefined;

  const validSubAddOnProducts = subscriptionAddOnProducts?.filter(
    (product) => product?.forSale,
  );

  if (validSubAddOnProducts?.length < 3) {
    // offer a console warning instead of throwing an error to avoid production app errors
    // return undefined to not show the SubALCAddOn component if there are fewer than the required 3 for-sale products
    // eslint-disable-next-line no-console
    console.warn(
      "Subscription Add-On config invalid: fewer than 3 for-sale products",
      {
        viableCount: validSubAddOnProducts?.length ?? 0,
        totalConfigured: subscriptionAddOnProducts?.length ?? 0,
      },
    );
    return undefined;
  }

  const filteredSubscriptionAddOn: SubscriptionAddOnProjection = {
    ...subscriptionAddOn,
    products: validSubAddOnProducts,
  };

  return ({ subscriptionId }) =>
    useSubALCAddOns({
      subscriptionId,
      subscriptionAddOn: filteredSubscriptionAddOn,
      region,
      lang,
    });
};
